import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { CATALOG_NAMESPACE, CATALOG_NAMESPACES, catalogResources } from './catalogs.js';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGE_CODES,
  resolveLanguage,
} from './localeManifest.js';

export const i18nOptions = Object.freeze({
  resources: catalogResources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGE_CODES],
  load: 'languageOnly',
  cleanCode: true,
  ns: [...CATALOG_NAMESPACES],
  defaultNS: CATALOG_NAMESPACE,
  returnObjects: true,
  returnNull: false,
  returnEmptyString: false,
  interpolation: {
    // React escapes rendered values. Escaping here as well would render HTML
    // entities to the user instead of the original interpolated value.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
  initAsync: false,
});

const i18n = createInstance();

export const i18nReady = i18n
  .use(initReactI18next)
  // i18next normalizes a few top-level options internally, so give it a
  // mutable copy while retaining an immutable exported reference for tests.
  .init({ ...i18nOptions });

// Load resources without changing the active language. Once bundles become
// lazy, a slow superseded load must never commit its locale, even temporarily.
// The generation check immediately before `changeLanguage` makes that commit a
// latest-request-only operation while still allowing independent loads to run
// concurrently.
let languageChangeGeneration = 0;

export const changeI18nLanguage = async (language) => {
  const resolvedLanguage = resolveLanguage(language);
  const generation = ++languageChangeGeneration;

  await i18n.loadLanguages(resolvedLanguage);

  if (generation !== languageChangeGeneration) return resolvedLanguage;
  if (i18n.resolvedLanguage !== resolvedLanguage) {
    await i18n.changeLanguage(resolvedLanguage);
  }
  return resolvedLanguage;
};

export const translate = (language, key, params = {}) => i18n.t(key, {
  lng: resolveLanguage(language),
  ns: CATALOG_NAMESPACE,
  ...(params || {}),
});

export { CATALOG_NAMESPACE };
export default i18n;
